import { PausedMarker } from "../you/reopen";
import type { Conversation } from "../../lib/conversations";

/**
 * The one-word state on a conversation row.
 *
 * Two vocabularies for the same ten states: the full sentence where there is
 * room (a header, the desk) and a short form where there is not (a list row).
 * They are the same fact, so the short form carries the long one as its
 * `title` — a chip that says "On you" can still explain itself.
 *
 * TWO OF THE NINE ARE ABOUT A DECISION THE OWNER MADE, not about where a knock
 * got to on its own. `saved` is the override made visible: the doorman said no
 * and the owner kept it anyway, and the chip says both halves of that so the
 * override can never be mistaken for a pass. `rejected` is its opposite number,
 * and it is deliberately NOT the same word as `away`: "Turned away" is what
 * happened to a knock of YOURS somewhere else, and "Rejected" is what YOU did to
 * somebody at your own door. Same stage, opposite sides of it, and a list that
 * used one word for both would read as if the reader were the one being refused.
 */
export type ChipKind =
  | "in"
  | "sent"
  | "through"
  | "connected"
  | "hearing"
  | "away"
  | "rejected"
  | "saved"
  | "stopped"
  | "unrecommended"
  | "live"
  | "paused"
  | "held"
  | "preknock"
  | "draft";

const FULL: Record<ChipKind, string> = {
  in: "Waiting on you",
  // Drawn with the door's name wherever there is one (`chipWord`); this is the
  // word for a surface that has none.
  sent: "Sent to them",
  through: "Let through",
  connected: "Connected — you knocked on each other",
  hearing: "Hearing in progress",
  away: "Turned away",
  rejected: "Rejected",
  saved: "Saved — didn't pass your Nob",
  stopped: "Stopped by your doorman",
  unrecommended: "Nob didn't recommend this",
  live: "Live",
  paused: "Paused",
  held: "Holding a spot",
  preknock: "Pre-knock",
  draft: "Not sent yet",
};

const SHORT: Record<ChipKind, string> = {
  in: "On you",
  sent: "Sent",
  through: "Through",
  connected: "Connected",
  hearing: "Hearing",
  away: "Away",
  rejected: "Rejected",
  saved: "Saved — didn't pass",
  stopped: "Stopped",
  unrecommended: "Not recommended",
  live: "Live",
  paused: "Paused",
  held: "Held",
  preknock: "Pre-knock",
  draft: "Draft",
};

/**
 * `doorLive` only matters for the owner's own row: every other row is about
 * somebody else's knock, whose state does not change when the door is paused.
 */
export function doorChipKind(conv: Conversation, doorLive: boolean): ChipKind {
  if (conv.kind === "own-nob") return doorLive ? "live" : "paused";
  // Before the other stages: a held spot is not at the door, and every clause
  // below would read it as one state of a knock (FR-6.3).
  //
  // TWO CHIPS FOR ONE STAGE, told apart by who is holding the spot — the same
  // split "Turned away"/"Rejected" makes above, and for the same reason. "Held"
  // is what the OWNER sees on somebody standing at their handle from before
  // they opened. "Pre-knock" is what the READER sees on a spot of their own,
  // and "Held" would have been a shrug there: what the row is about is that
  // there is no door there yet and no knock has happened (owner, 2026-08-27).
  if (conv.stage === "pre-knock") return conv.direction === "outgoing" ? "preknock" : "held";
  // And before the direction clause, for the same reason: a knock still being
  // written is outgoing, and "Waiting on them" would say a thing has been sent
  // that nobody has sent (owner, 2026-08-26).
  if (conv.stage === "composing") return "draft";
  // The pair's fact outranks the row's (§9): a door open both ways is more than
  // "let through", and it is the thing the row exists to say.
  if (conv.connected) return "connected";
  if (conv.stage === "through") return "through";
  // At the door with no read published (§6.3's first row) — the badge says what
  // is actually happening. It is asked before "turned away" and "sent" and after
  // the two decided states, because a decision outranks it (§3).
  if (conv.inFlight) return "hearing";
  // One stage, two chips, told apart by who did it — see the note above.
  if (conv.stage === "turned-away") return conv.direction === "incoming" ? "rejected" : "away";
  /**
   * THE KNOCKER'S OWN KNOCK, AFTER THE READ, SAYS THE READ WAS SENT (ruling
   * R-0925-1 (1)). It said "Waiting on them" in the header, "On them" in the
   * panel and "Still at their door" in the Stage row — three words for one
   * state, none of them what had happened. What happened is a fact: Nob sent
   * its read to the owner, and what comes next is theirs. It is not a verdict
   * and says nothing of what the read recommended.
   *
   * An outgoing knock at the door with no read is the hearing, whatever else is
   * true of it: the one such row that is not `inFlight` is a knock this console
   * has just sent, one round trip before the server's own row arrives.
   */
  if (conv.direction === "outgoing") return conv.assessed ? "sent" : "hearing";
  /**
   * The override, and only where there is one to see.
   *
   * A Save on a knock that ALREADY cleared the bar changes nothing about how the
   * row should read — it was going to be in Passed regardless — so it stays
   * "On you". The chip appears only when the Save is what is holding the row in
   * the list, which is exactly when the owner needs to be reminded they put it
   * there.
   */
  if (conv.saved && conv.standing === "needs-attention") return "saved";
  /**
   * NOB'S CALL, ON THE ROW (ruling R-0925-1 (2)). A knock Nob did not
   * recommend is still the owner's to decide, but it no longer wears the same
   * "On you" as one it did: six rows of "On you" over a door where half had
   * not cleared the bar told the owner nothing. A guard stop is named apart
   * from the rest — an abusive knocker must not look like a waiting one.
   */
  if (conv.direction === "incoming" && conv.guardClosed) return "stopped";
  if (conv.direction === "incoming" && conv.notRecommended) return "unrecommended";
  return "in";
}

export function DoorChip({
  conv,
  doorLive,
  compact = false,
  reopenable = false,
  responsive = false,
}: {
  conv: Conversation;
  doorLive: boolean;
  compact?: boolean;
  /**
   * A "Paused" chip on this surface is a way back up (owner item 13).
   *
   * Only `own-nob` can ever read `paused` — every other row is about somebody
   * else's knock — so this could have been inferred. It is a prop anyway,
   * because pressing it opens a SHELL sheet, and a chip that reached for the
   * shell's context on its own would stop rendering anywhere outside it.
   */
  reopenable?: boolean;
  /**
   * The short word on a phone and the full one from `sm` up — one chip, so
   * there is one thing to press (wave 82). The chat header's, where the full
   * word took the width the door's name needed at 375.
   */
  responsive?: boolean;
}) {
  const kind = doorChipKind(conv, doorLive);
  const full = chipWord(kind, conv);
  const word = responsive ? (
    <>
      <span className="sm:hidden">{SHORT[kind]}</span>
      <span className="hidden sm:inline">{full}</span>
    </>
  ) : compact ? (
    SHORT[kind]
  ) : (
    full
  );
  if (reopenable && kind === "paused") {
    return (
      <PausedMarker>
        <span data-chip={kind} title={full}>
          {word}
        </span>
      </PausedMarker>
    );
  }
  return (
    <span data-chip={kind} title={full}>
      {word}
    </span>
  );
}

/** The full word, with the door's name in it where the state is about them. */
export function chipWord(kind: ChipKind, conv: Pick<Conversation, "name">): string {
  if (kind === "sent" && conv.name.trim()) return sentTo(conv.name);
  return FULL[kind];
}

/** "Sent to Sam" — the state of a knock whose read has gone to its owner (R-0925-1 (1)). */
export function sentTo(name: string): string {
  return `Sent to ${name.trim()}`;
}

export { FULL as CHIP_FULL, SHORT as CHIP_SHORT };
